import {
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  Input,
  Warning,
  theme
} from '@lmc/ui-kit'
import { Button, Heading, Text } from '@lmc/ui-kit/src/general'
import { Divider, FormInstance, Modal, RadioChangeEvent, Upload } from 'antd'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { dateFormat } from '../../../utils/utils'
import { ResidentDnaCprDetails, User, UserRole } from '@lmc/ts-common'
import { useEffect, useState } from 'react'
import { SupDatePicker } from '../../../styledComponents'
import styled from 'styled-components'
import { generalConstants } from 'locale/general'
import { UploadOutlined } from '@ant-design/icons'
import { RcFile } from 'antd/es/upload'
import { useSelector } from 'react-redux'
import { UploadFile } from 'antd/lib/upload'
import dayjs from 'dayjs'
import { FormWarning } from 'components/forms/FormWarning'

type DnacprFormCardProps = {
  form: FormInstance
  dnacprValue: boolean | null | undefined
  dnacprFormUrl: string | undefined
  setSelectedFile: (file?: RcFile) => void
}

export const DnacprFormCard = ({
  form,
  dnacprValue,
  dnacprFormUrl,
  setSelectedFile
}: DnacprFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [showDnacprReviewDate, setShowDnacprReviewDate] = useState(
    form.getFieldValue('dnacprDetails') === ResidentDnaCprDetails.underReview
  )
  const [fileList, setFileList] = useState<UploadFile[]>([
    {
      uid: '-1',
      name: 'DNACPR Form',
      status: 'done',
      url: dnacprFormUrl
    }
  ])
  const [showFormModal, setShowFormModal] = useState(false)
  const [showDnacprStartDate, setShowDnacprStartDate] = useState(false)
  const [showFormFields, setShowFormFields] = useState(dnacprValue)
  const [showDnacprStartDateWarning, setShowDnacprStartDateWarning] =
    useState(false)

  const checkIfStartDateWarningNeeded = () => {
    const dnacprStartDate = form.getFieldValue('dnacprStartDate')
    if (dnacprStartDate) {
      const isWarning = dayjs().isBefore(dnacprStartDate, 'day')
      setShowDnacprStartDateWarning(isWarning)
    } else {
      setShowDnacprStartDateWarning(false)
    }
  }

  const user = useSelector(
    (state: { global: { user: User & { privacyOfficer?: boolean } } }) =>
      state.global.user
  )

  useEffect(() => {
    checkIfStartDateWarningNeeded()
  }, [])

  useEffect(() => {
    const initialDnacprDetails = form.getFieldValue('dnacprDetails')
    const initialDnacpr = form.getFieldValue('dnacpr')
    setShowDnacprReviewDate(
      initialDnacprDetails === ResidentDnaCprDetails.underReview
    )
    setShowDnacprStartDate(initialDnacpr === true)
  }, [form])

  const handleDnacprDetailsChange = (e: RadioChangeEvent) => {
    const isUnderReview = e.target.value === ResidentDnaCprDetails.underReview
    setShowDnacprReviewDate(isUnderReview)

    if (!isUnderReview) {
      form.setFieldsValue({ dnacprReviewDate: null })
    }
  }

  const handleAttemptCprChange = (e: RadioChangeEvent) => {
    if (e.target.value === true) {
      setShowFormModal(true)
      setShowDnacprStartDate(true)
    } else {
      setShowFormFields(false)
      setShowDnacprStartDate(false)
    }
  }

  const handleModalConfirm = () => {
    setShowFormModal(false)
    form.setFieldsValue({ dnacpr: true })
    setShowFormFields(true)
  }

  const handleRemoveFile = () => {
    setSelectedFile(undefined)
    setFileList([])
    form.setFieldValue('dnacprForm', null)
  }

  const role = user?.role
  const isCareHomeAdmin = role === UserRole.careHomeAdmin

  if (!isCareHomeAdmin) {
    return null
  }

  return (
    <>
      <FormCard title={t(supConstants.DNACPR)}>
        <FormItem
          label={<Text>{t(supConstants.CPR_Q)}</Text>}
          name="dnacpr"
          rules={[
            {
              required: true,
              message: t(supConstants.DNACPR_ERROR)
            }
          ]}
        >
          <FormRadioGroup
            value={form.getFieldValue('dnacpr')}
            onChange={handleAttemptCprChange}
          >
            <FormRadio value={false} text={t(supConstants.ATTEMPT_CPR)} />
            <FormRadio value text={t(supConstants.DO_NOT_CPR)} />
          </FormRadioGroup>
        </FormItem>
        {showFormFields && (
          <>
            <FormItem label={<Text>{t(supConstants.DNACPR_FORM)}</Text>}>
              <Upload
                fileList={dnacprFormUrl ? fileList : undefined}
                beforeUpload={(file) => {
                  setSelectedFile(file)
                  return false
                }}
                onRemove={handleRemoveFile}
              >
                <Button>
                  <UploadOutlined />
                  {t(supConstants.UPLOAD_FORM)}
                </Button>
              </Upload>
            </FormItem>
            <FormItem name="dnacprForm" hidden />
            <FormItem
              label={<Text>{t(supConstants.DNACPR_FORM_LOCATION)}</Text>}
              name="dnacprFormPhysicalLocation"
            >
              <Input
                placeholder={t(supConstants.DNACPR_FORM_LOCATION_PLACEHOLDER)}
              />
            </FormItem>
          </>
        )}
        {showDnacprStartDate && (
          <>
            <FormItem label={<Text>{t(supConstants.DNACPR_START_DATE)}</Text>}>
              <FormItem
                name="dnacprStartDate"
                getValueProps={(value) => ({
                  value: value ? dayjs.utc(value) : undefined
                })}
                normalize={(value) => {
                  return value ? dayjs(value).utc().format() : null
                }}
              >
                <SupDatePicker
                  format="DD-MM-YYYY"
                  onChange={() => checkIfStartDateWarningNeeded()}
                />
              </FormItem>
              {showDnacprStartDateWarning && (
                <FormWarningWrapper>
                  <FormWarning
                    message={t(supConstants.DNACPR_START_DATE_WARNING)}
                  />
                </FormWarningWrapper>
              )}
            </FormItem>
            <FormItem
              label={<Text>{t(supConstants.DNACPR_DECISION_MADE_DATE)}</Text>}
              colon={false}
            >
              <FormItem
                name="dnacprDecisionMadeDate"
                getValueProps={(value) => ({
                  value: value ? dayjs.utc(value) : undefined
                })}
                normalize={(value) => {
                  return value ? dayjs(value).utc().format() : null
                }}
              >
                <SupDatePicker format="DD-MM-YYYY" />
              </FormItem>
            </FormItem>
          </>
        )}
        <FormItem
          name="dnacprDetails"
          label={<Text>{t(supConstants.REVIEW_DATE)}</Text>}
        >
          <FormRadioGroup onChange={handleDnacprDetailsChange}>
            <FormRadio
              value={ResidentDnaCprDetails.indefiniteDecision}
              text={t(supConstants.INDEFINITE_DECISION)}
            />
            <FormRadio
              value={ResidentDnaCprDetails.underReview}
              text={t(supConstants.DNACPR_REVIEW_DATE)}
            />
          </FormRadioGroup>
        </FormItem>

        {showDnacprReviewDate && (
          <FormItem
            label={<Text>{t(supConstants.REVIEW_DATE)}</Text>}
            rules={[
              {
                required: true,
                message: t(supConstants.DNACPR_REVIEW_DATE_ERROR)
              }
            ]}
            name="dnacprReviewDate"
          >
            <SupDatePicker placeholder="" format={dateFormat} />
          </FormItem>
        )}
      </FormCard>
      <Modal
        closable={false}
        open={showFormModal}
        footer={
          <FooterContainer>
            <Button
              onClick={() => {
                setShowFormModal(false)
                form.setFieldsValue({ dnacpr: null })
              }}
            >
              {t(generalConstants.CANCEL)}
            </Button>
            <Button variant="primary" onClick={handleModalConfirm}>
              {t(supConstants.CONFIRM)}
            </Button>
          </FooterContainer>
        }
      >
        <TitleContainer>
          <Warning size={22} color={theme.color.status.warning} />
          <ModalTitle level={3}>
            {t(supConstants.DNACPR_MODAL_TITLE)}
          </ModalTitle>
        </TitleContainer>
        <Divider />
        <ModalContent>{t(supConstants.DNACPR_MODAL_CONTENT)}</ModalContent>
        <Divider />
      </Modal>
    </>
  )
}

const FooterContainer = styled.div`
  display: flex;
  gap: 8px;
`

const ModalTitle = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const FormWarningWrapper = styled.div`
  margin-top: 20px;
`

const ModalContent = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`
